import { Handle, type NodeProps, Position } from "@xyflow/react";
import type { StepNodeData } from "../../lib/yaml-serializer.js";

const CATEGORY_COLORS: Record<string, string> = {
	"llm-inference": "var(--forge-color-cat-ai)",
	"rag-retrieval": "var(--forge-color-cat-ai)",
	"sql-query": "var(--forge-color-cat-data)",
	"knowledge-graph-query": "var(--forge-color-cat-data)",
	"rest-api": "var(--forge-color-cat-integration)",
	"slack-message": "var(--forge-color-cat-comms)",
	transform: "var(--forge-color-cat-transform)",
};

const DEFAULT_COLOR = "var(--forge-color-cat-custom)";

const STATUS_COLORS: Record<string, string> = {
	pending: "var(--forge-color-text-muted)",
	running: "var(--forge-color-accent)",
	completed: "var(--forge-color-success)",
	failed: "var(--forge-color-error)",
	skipped: "var(--forge-color-text-muted)",
};

function getCategoryColor(stepType: string): string {
	return CATEGORY_COLORS[stepType] ?? DEFAULT_COLOR;
}

function formatDuration(ms: number): string {
	return `${(ms / 1000).toFixed(1)}s`;
}

export function StepNode({ data, selected }: NodeProps) {
	const nodeData = data as unknown as StepNodeData;
	const categoryColor = getCategoryColor(nodeData.stepType);
	const execState = nodeData.executionState;

	// In monitor mode, border/glow color is driven by status; otherwise by category
	const color = execState ? (STATUS_COLORS[execState.status] ?? categoryColor) : categoryColor;

	const isRunning = execState?.status === "running";
	const isFailed = execState?.status === "failed";
	const isCompleted = execState?.status === "completed";

	// Build status badge label
	let statusLabel: string | null = null;
	if (execState) {
		if (isCompleted && execState.duration !== null) {
			statusLabel = formatDuration(execState.duration);
		} else {
			statusLabel = execState.status;
		}
	}

	// Box shadow
	let boxShadow = "var(--forge-shadow-sm)";
	if (selected || isRunning || isFailed) {
		boxShadow = "var(--forge-shadow-lg)";
	}

	return (
		<div
			className="relative rounded-xl overflow-hidden transition-all duration-150"
			style={{
				background: "var(--forge-color-surface)",
				border: `1px solid ${execState ? color : selected ? color : `${color}33`}`,
				width: 200,
				boxShadow,
				transform: selected && !execState ? "translateY(-1px)" : "none",
				animation: isRunning ? "pulse-glow 2s ease-in-out infinite" : undefined,
			}}
		>
			{/* Header */}
			<div
				className="flex items-center gap-2 px-3 py-2"
				style={{ borderBottom: `1px solid ${color}1a` }}
			>
				<div
					className="w-1.5 h-1.5 rounded-full flex-shrink-0"
					style={{ background: color, boxShadow: `0 0 6px ${color}` }}
				/>
				<span
					className="text-[10px] font-semibold uppercase tracking-widest flex-1 min-w-0 truncate"
					style={{ color }}
				>
					{nodeData.stepType}
				</span>
				{/* Status badge */}
				{statusLabel !== null && (
					<span
						data-testid="status-badge"
						className="text-[9px] font-semibold uppercase tracking-wider px-1.5 py-0.5 rounded-full flex-shrink-0"
						style={{
							background: `${color}22`,
							color,
							border: `1px solid ${color}44`,
						}}
					>
						{statusLabel}
					</span>
				)}
			</div>

			{/* Body */}
			<div className="px-3 py-2">
				<div className="text-[13px] font-semibold text-text-primary">{nodeData.stepId}</div>
				{nodeData.description && (
					<div className="text-[11px] text-text-muted mt-0.5">{nodeData.description}</div>
				)}
				{/* Retry count for failed steps */}
				{isFailed && execState && execState.maxAttempts > 1 && (
					<div
						data-testid="retry-count"
						className="text-[10px] mt-1"
						style={{ color: STATUS_COLORS.failed }}
					>
						{`Attempt ${execState.attempt}/${execState.maxAttempts}`}
					</div>
				)}
			</div>

			{/* Progress bar for running steps */}
			{isRunning && (
				<div
					data-testid="progress-bar"
					className="h-0.5 relative overflow-hidden"
					style={{ background: `${color}22` }}
				>
					<div
						className="absolute inset-y-0 w-1/2"
						style={{
							background: color,
							animation: "progress-slide 1.5s ease-in-out infinite",
						}}
					/>
				</div>
			)}

			{/* Ports */}
			<Handle
				type="target"
				position={Position.Left}
				className="!bg-surface"
				style={{ border: `2px solid ${color}`, width: 10, height: 10 }}
			/>
			<Handle
				type="source"
				position={Position.Right}
				className="!bg-surface"
				style={{ border: `2px solid ${color}`, width: 10, height: 10 }}
			/>
		</div>
	);
}
